import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { equipment as equipmentApi } from '../../services/api';
import MediaManager from './MediaManager';
import { Chip, ChipsInput, ConfirmDialog, Field, Modal, Switch, Thumb, errorList } from './shared';

/*
 * Criar ou editar uma máquina ou peça de equipamento. O que a distingue de uma lista de nomes é o
 * "o ginásio tem esta máquina": uma máquina que o ginásio não tem deixa de ser sugerida como
 * alternativa a um exercício.
 */

const emptyForm = () => ({ name: '', nameEn: '', aliases: [], kind: '', family: '', description: '', isAvailable: true, isActive: true });

const toForm = (d) => ({
  name: d.name || '', nameEn: d.nameEn || '', aliases: [...(d.aliases || [])], kind: d.kind || '', family: d.family || '',
  description: d.description || '', isAvailable: d.isAvailable !== false, isActive: d.isActive !== false,
});

const toPayload = (f) => ({
  name: f.name.trim(), nameEn: f.nameEn.trim(), aliases: f.aliases, kind: f.kind, family: f.family,
  description: f.description.trim(), isAvailable: f.isAvailable, isActive: f.isActive,
});

export default function EquipmentEditor({ equipmentId, meta, onClose, onSaved, onDeleted }) {
  const [id, setId] = useState(equipmentId);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [initial, setInitial] = useState(() => JSON.stringify(emptyForm()));
  const [loading, setLoading] = useState(!!equipmentId);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const bodyRef = useRef(null);

  const canEdit = meta.canManage;
  const dirty = JSON.stringify(form) !== initial;

  const adopt = useCallback((d) => {
    const next = toForm(d);
    setDetail(d);
    setForm(next);
    setInitial(JSON.stringify(next));
  }, []);

  useEffect(() => {
    if (!equipmentId) return undefined;
    let alive = true;
    setLoading(true);
    equipmentApi.get(equipmentId)
      .then(({ data }) => { if (alive) adopt(data); })
      .catch((e) => { if (alive) setLoadError(errorList(e, 'Não foi possível carregar o equipamento.').join(' ')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [equipmentId, adopt]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const close = () => (dirty && canEdit ? setConfirm('discard') : onClose());

  const save = async () => {
    setErrors([]);
    if (!form.name.trim()) { setErrors(['O nome é obrigatório.']); return; }
    if (!id && !form.kind) { setErrors(['Escolha o tipo de equipamento.']); return; }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const { data } = id ? await equipmentApi.update(id, payload) : await equipmentApi.create(payload);
      setId(data.id);
      adopt(data);
      onSaved(data);
      toast.success(id ? 'Equipamento guardado' : 'Equipamento criado');
    } catch (e) {
      setErrors(errorList(e));
      bodyRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await equipmentApi.reset(id);
      adopt(data);
      onSaved(data);
      toast.success('Equipamento reposto como no catálogo');
    } catch (e) {
      setErrors(errorList(e));
    } finally {
      setSaving(false);
      setConfirm(null);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await equipmentApi.remove(id);
      toast.success('Equipamento apagado');
      onDeleted(id);
    } catch (e) {
      setErrors(errorList(e));
      setConfirm(null);
      setSaving(false);
    }
  };

  const footer = canEdit ? (
    <>
      <div className="grow">
        {id && detail?.source === 'catalog' && detail.isCustomized && (
          <button type="button" className="lib-btn" disabled={saving} onClick={() => setConfirm('reset')}>Repor do catálogo</button>
        )}
        {id && detail && detail.source !== 'catalog' && (
          <button type="button" className="lib-btn danger" disabled={saving} onClick={() => setConfirm('delete')}>Apagar</button>
        )}
      </div>
      <button type="button" className="lib-btn" disabled={saving} onClick={close}>Fechar</button>
      <button type="button" className="lib-btn primary" disabled={saving || (!dirty && !!id)} onClick={save}>
        {saving ? 'A guardar…' : id ? 'Guardar' : 'Criar equipamento'}
      </button>
    </>
  ) : <button type="button" className="lib-btn" onClick={onClose}>Fechar</button>;

  return (
    <>
      <Modal title={id ? (detail?.name || 'Equipamento') : 'Novo equipamento'} onClose={close} footer={footer} narrow>
        <div ref={bodyRef}>
          {loading && <div className="lib-loading" role="status">A carregar…</div>}
          {loadError && <div className="lib-notice bad" role="alert">{loadError}</div>}

          {!loading && !loadError && (
            <>
              {errors.length > 0 && (
                <div className="lib-notice bad" role="alert">
                  {errors.length === 1 ? errors[0] : <><strong>Corrija antes de guardar:</strong><ul>{errors.map((m) => <li key={m}>{m}</li>)}</ul></>}
                </div>
              )}
              {detail?.source === 'catalog' && (
                <div className={`lib-notice${detail.isCustomized ? ' warn' : ''}`}>
                  {detail.isCustomized
                    ? 'Equipamento do catálogo, editado neste ginásio: o catálogo já não lhe toca. Pode repô-lo.'
                    : 'Equipamento do catálogo. Mudar a disponibilidade não conta como edição; mudar o nome, o tipo ou a descrição, sim.'}
                </div>
              )}

              <div className="lib-field">
                <span className="lib-label">O ginásio tem esta máquina?</span>
                <Switch checked={form.isAvailable} onChange={(isAvailable) => set({ isAvailable })} disabled={!canEdit}
                  onLabel="Sim, temos" offLabel="Não temos" label="O ginásio tem esta máquina" />
                <span className="lib-hint">Sem a máquina, os exercícios que dependem dela não são sugeridos como alternativa.</span>
              </div>

              <div className="lib-row">
                <Field label="Nome *" htmlFor="eq-name">
                  <input id="eq-name" className="lib-input" value={form.name} maxLength={120} disabled={!canEdit} required onChange={(e) => set({ name: e.target.value })} />
                </Field>
                <Field label="Nome em inglês" htmlFor="eq-nameen">
                  <input id="eq-nameen" className="lib-input" value={form.nameEn} maxLength={120} disabled={!canEdit} onChange={(e) => set({ nameEn: e.target.value })} />
                </Field>
              </div>
              <Field label="Outros nomes" hint="Como lhe chamam no ginásio. Entram na pesquisa.">
                <ChipsInput values={form.aliases} onChange={(aliases) => set({ aliases })} placeholder="Ex.: prensa 45" ariaLabel="Outros nomes" />
              </Field>
              <div className="lib-row">
                <Field label={id ? 'Tipo' : 'Tipo *'} htmlFor="eq-kind">
                  <select id="eq-kind" className="lib-select" value={form.kind} disabled={!canEdit} onChange={(e) => set({ kind: e.target.value })}>
                    <option value="">—</option>
                    {meta.equipmentKinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
                  </select>
                </Field>
                <Field label="Zona do ginásio" htmlFor="eq-family">
                  <select id="eq-family" className="lib-select" value={form.family} disabled={!canEdit} onChange={(e) => set({ family: e.target.value })}>
                    <option value="">—</option>
                    {meta.equipmentFamilies.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Descrição" htmlFor="eq-desc">
                <textarea id="eq-desc" className="lib-textarea" value={form.description} maxLength={1000} disabled={!canEdit} onChange={(e) => set({ description: e.target.value })} />
              </Field>
              <label className="lib-check">
                <input type="checkbox" checked={form.isActive} disabled={!canEdit} onChange={(e) => set({ isActive: e.target.checked })} />
                Visível na biblioteca (desmarcar esconde-a de todos, sem a apagar)
              </label>

              <h3 style={{ fontSize: '0.95rem', margin: '16px 0 8px' }}>Imagens</h3>
              {id && detail ? (
                <MediaManager api={equipmentApi} ownerId={id} media={detail.media} limits={meta.media} canEdit={canEdit}
                  onChange={(media) => { setDetail((d) => ({ ...d, media })); onSaved({ ...detail, media, imageUrl: media.find((m) => m.isPrimary)?.url ?? null }); }} />
              ) : (
                <div className="lib-notice">Guarde o equipamento para lhe poder carregar imagens.</div>
              )}

              {detail && (
                <>
                  <h3 style={{ fontSize: '0.95rem', margin: '16px 0 8px' }}>Exercícios que usam esta máquina ({detail.exercises.length})</h3>
                  {detail.exercises.length === 0 ? (
                    <div className="lib-empty">Nenhum exercício usa esta máquina.</div>
                  ) : (
                    <div className="lib-picklist" role="list">
                      {detail.exercises.map((x) => (
                        <div key={x.id} className="lib-pick" role="listitem" style={{ opacity: x.isActive ? 1 : 0.6 }}>
                          <Thumb src={x.imageUrl} />
                          <span style={{ fontWeight: 600 }}>{x.name}</span>
                          <span className="lib-chips">
                            {x.isPrimary ? <Chip tone="accent">Principal</Chip> : <Chip>Acessório</Chip>}
                            {!x.isActive && <Chip tone="bad">Desativado</Chip>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="lib-chips" style={{ marginTop: 16 }}>
                    <Chip>{detail.slug}</Chip>
                    {detail.catalogVersion != null && <Chip>Catálogo v{detail.catalogVersion}</Chip>}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal>

      {confirm === 'discard' && (
        <ConfirmDialog title="Descartar alterações?" message="Tem alterações por guardar. Se fechar agora, perdem-se." confirmLabel="Descartar" danger
          onConfirm={onClose} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'reset' && (
        <ConfirmDialog title="Repor do catálogo" busy={saving} confirmLabel="Repor"
          message="A máquina volta a ser como o catálogo a define (nome, tipo, zona, descrição). As imagens que carregou ficam."
          onConfirm={reset} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog title="Apagar equipamento" busy={saving} confirmLabel="Apagar" danger
          message="A máquina e as imagens que carregou são apagadas. Se algum exercício a usa, o servidor recusa — retire-a dos exercícios primeiro."
          onConfirm={remove} onCancel={() => setConfirm(null)} />
      )}
    </>
  );
}
